/**
 * InventorySystem - Manages item inventory, equipment, and scrapping
 *
 * Wraps GameState inventory methods and emits events for cross-system communication.
 * Part of SYS-02 requirement.
 *
 * Following the Strangler Fig pattern from Phase 3:
 * - Delegates all operations to GameState internally
 * - Emits events for cross-system communication
 * - Does NOT modify GameState.js - pure extraction
 */

import { eventBus, GameEvents } from '../utils/EventBus.js';

export class InventorySystem {
    /**
     * @param {GameState} gameState - Reference to main game state
     */
    constructor(gameState) {
        this.gameState = gameState;
    }

    // ===== Item Management =====

    /**
     * Add an item to inventory
     * @param {Object} itemObject - The item object to add
     * @returns {boolean} True if added successfully
     */
    addItem(itemObject) {
        return this.gameState.addItem(itemObject);
    }

    /**
     * Check if an item exists in inventory
     * @param {string} itemId - The item ID
     * @returns {boolean} True if item exists
     */
    hasItem(itemId) {
        return this.gameState.hasItem(itemId);
    }

    /**
     * Get an item by ID
     * @param {string} itemId - The item ID
     * @returns {Object|undefined} The item object or undefined
     */
    getItemById(itemId) {
        return this.gameState.getItemById(itemId);
    }

    /**
     * Get all inventory items
     * @returns {Array} Array of item objects
     */
    getInventoryItems() {
        return this.gameState.getInventoryItems();
    }

    // ===== Equipment Management =====

    /**
     * Equip an item to a slot
     * @param {string} itemId - The item ID to equip
     * @param {string} slotType - The slot to equip to (dashboard, keychain, rearview)
     * @returns {boolean} True if equipped successfully
     */
    equipItem(itemId, slotType) {
        const result = this.gameState.equipItem(itemId, slotType);

        if (result) {
            eventBus.emit(GameEvents.ITEM_EQUIPPED, {
                itemId,
                slot: slotType,
                item: this.getItemById(itemId)
            });
        }

        return result;
    }

    /**
     * Unequip an item from a slot
     * @param {string} slotType - The slot to unequip
     */
    unequipSlot(slotType) {
        const previousItem = this.getEquippedItems()[slotType];

        this.gameState.unequipSlot(slotType);

        if (previousItem) {
            eventBus.emit(GameEvents.ITEM_UNEQUIPPED, {
                slot: slotType,
                item: previousItem,
                itemId: previousItem.id
            });
        }
    }

    /**
     * Get all equipped items
     * @returns {Object} Object mapping slot types to item objects
     */
    getEquippedItems() {
        return this.gameState.getEquippedItems();
    }

    // ===== Scrapping =====

    /**
     * Scrap an item for wavedust
     * @param {string} itemId - The item ID
     * @returns {Object} { success, base, bonus, total }
     */
    scrapItem(itemId) {
        return this.gameState.scrapItem(itemId);
    }

    /**
     * Get the scrap value for an item by ID
     * @param {string} itemId - The item ID
     * @returns {number} Wavedust value
     */
    getItemScrapValue(itemId) {
        return this.gameState.getItemScrapValue(itemId);
    }

    /**
     * Get the scrap value for an item object directly
     * @param {Object} item - The item object
     * @returns {number} Wavedust value
     */
    getItemScrapValueDirect(item) {
        return this.gameState.getItemScrapValueDirect(item);
    }
}

// ===== Singleton Pattern =====

let _instance = null;

/**
 * Get the InventorySystem singleton instance
 * @param {GameState} [gameState] - GameState reference (required on first call)
 * @returns {InventorySystem|null} The instance or null if not initialized
 */
export function getInventorySystem(gameState) {
    if (!_instance && gameState) {
        _instance = new InventorySystem(gameState);
    }
    return _instance;
}

/**
 * Reset the singleton instance (for testing)
 */
export function resetInventorySystem() {
    _instance = null;
}
